import LiteratureCover from "@modules/Literature/LiteratureCover";
import LiteratureTitle from "@modules/Literature/LiteratureTitle";
import { FrontSiteRoutes } from "@routes/urls";
import _get from "lodash/get";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Card, Label, Icon } from "semantic-ui-react";
import { invenioConfig, getDisplayVal } from "@config";
import { Link } from "react-router-dom";
import { Truncate } from "@components/Truncate";
import { renderSubtitle } from "@modules/Document/utils";

class DocumentCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showLocationInfo: false,
    };
  }

  // Medium type icon and color mapping
  getMediumTypeConfig = (type) => {
    // Normalize the type for better matching
    const normalizedType = type?.toString().trim();

    const configs = {
      // Paper formats
      PAPER: { icon: "file text", color: "green", title: "Paper" },

      // CD/DVD formats
      CDROM: { icon: "radio", color: "green", title: "CD-ROM" },
      "CD-ROM": { icon: "radio", color: "green", title: "CD-ROM" },
      "cd-rom": { icon: "radio", color: "green", title: "CD-ROM" },
      "Cd-rom": { icon: "circradiole", color: "green", title: "CD-ROM" },
      "CD ROM": { icon: "radio", color: "green", title: "CD-ROM" },
      DVD: { icon: "disk", color: "green", title: "DVD" },
      dvd: { icon: "disk", color: "green", title: "DVD" },

      // Video formats
      VHS: { icon: "film", color: "green", title: "VHS" },
      vhs: { icon: "film", color: "green", title: "VHS" },
      VIDEO: { icon: "video", color: "green", title: "Video" },
      Video: { icon: "video", color: "green", title: "Video" },
      video: { icon: "video", color: "green", title: "Video" },

      // Audio formats
      AUDIOBOOK: { icon: "headphones", color: "green", title: "Audiobook" },
      Audiobook: { icon: "headphones", color: "green", title: "Audiobook" },
      audiobook: { icon: "headphones", color: "green", title: "Audiobook" },
      "AUDIO BOOK": { icon: "headphones", color: "green", title: "Audiobook" },
      "Audio Book": { icon: "headphones", color: "green", title: "Audiobook" },

      // Book formats
      HARDCOVER: { icon: "book", color: "green", title: "Hardcover" },
      Hardcover: { icon: "book", color: "green", title: "Hardcover" },
      hardcover: { icon: "book", color: "green", title: "Hardcover" },
      "HARD COVER": { icon: "book", color: "green", title: "Hardcover" },
      "Hard Cover": { icon: "book", color: "green", title: "Hardcover" },

      SOFTCOVER: { icon: "book open", color: "green", title: "Softcover" },
      Softcover: { icon: "book open", color: "green", title: "Softcover" },
      softcover: { icon: "book open", color: "green", title: "Softcover" },
      PAPERBACK: { icon: "book open", color: "green", title: "Paperback" },
      Paperback: { icon: "book open", color: "green", title: "Paperback" },
      paperback: { icon: "book open", color: "green", title: "Paperback" },

      // Digital formats
      EBOOK: { icon: "tablet alternate", color: "green", title: "E-book" },
      "E-BOOK": { icon: "tablet alternate", color: "green", title: "E-book" },
      Ebook: { icon: "tablet alternate", color: "green", title: "E-book" },
      "E-book": { icon: "tablet alternate", color: "green", title: "E-book" },
      ebook: { icon: "tablet alternate", color: "green", title: "E-book" },

      PDF: { icon: "file pdf", color: "green", title: "PDF" },
      pdf: { icon: "file pdf", color: "green", title: "PDF" },
    };

    // Direct match first
    if (configs[normalizedType]) {
      return configs[normalizedType];
    }

    // Try uppercase match
    const upperType = normalizedType?.toUpperCase();
    if (configs[upperType]) {
      return configs[upperType];
    }

    // Try lowercase match
    const lowerType = normalizedType?.toLowerCase();
    if (configs[lowerType]) {
      return configs[lowerType];
    }

    // Try removing spaces and hyphens for compound terms
    const cleanType = normalizedType?.toUpperCase().replace(/[\s-]/g, "");
    if (configs[cleanType]) {
      return configs[cleanType];
    }

    // Default fallback
    return {
      icon: "file alternate",
      color: "grey",
      title: normalizedType || "Unknown",
    };
  };

  handleMouseEnter = () => {
    this.setState({ showLocationInfo: true });
  };

  handleMouseLeave = () => {
    this.setState({ showLocationInfo: false });
  };

  renderImage = () => {
    const { data, volume } = this.props;
    const image = (
      <LiteratureCover
        isRestricted={_get(data, "metadata.restricted", false)}
        size="small"
        url={_get(data, "metadata.cover_metadata.urls.medium")}
      />
    );

    if (volume) {
      return (
        <div
          className="search-result-image"
          style={{
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Label floating color="black">
            Volume {volume}
          </Label>
          {image}
        </div>
      );
    }

    return (
      <div
        style={{
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          marginLeft: "-20px",
        }}
      >
        {image}
      </div>
    );
  };

  renderEItemTypeLabels = (eitems) => {
    if (eitems.total === 0) {
      return null;
    }

    const uniqueTypes = [
      ...new Set(eitems.hits.map((eitem) => eitem.eitem_type)),
    ];

    return (
      <div className="medium-type-icons">
        {uniqueTypes.map((type) => {
          const config = this.getMediumTypeConfig(type);
          return (
            <Icon
              key={type}
              name={config.icon}
              color={config.color}
              title={config.title}
              className="medium-type-icon"
            />
          );
        })}
      </div>
    );
  };

  getMediumDisplay = (item, documentMetadata) => {
    const itemMedium = _get(item, "medium");
    const docMedium = _get(documentMetadata, "medium");

    if (itemMedium) {
      return getDisplayVal("ITEMS.mediums", itemMedium) || itemMedium;
    }

    if (docMedium) {
      return getDisplayVal("DOCUMENTS.mediums", docMedium) || docMedium;
    }

    return "Not specified";
  };

  renderCirculationInfo = (metadata) => {
    // Calculate availability from live items data
    const items = metadata.items?.hits || [];
    const eitems = metadata.eitems || { total: 0, hits: [] };

    // Check if we have ANY items (physical OR electronic)
    const hasPhysicalItems = items.length > 0;
    const hasElectronicItems = eitems.total > 0;

    // If NO items at all (neither physical nor electronic)
    if (!hasPhysicalItems && !hasElectronicItems) {
      return (
        <div className="circulation-info-container circulation-info-fixed">
          <Label basic color="grey" className="circulation-label">
            <Icon name="book" />
            No items
          </Label>
        </div>
      );
    }

    // Handle physical items if available
    if (hasPhysicalItems) {
      // Count available items (not on loan and can circulate)
      const availableItems = items.filter((item) => {
        const isOnLoan = item._isOnLoan || false;
        const canCirculate =
          invenioConfig.ITEMS?.canCirculateStatuses?.includes(item.status) ||
          item.status === "CAN_CIRCULATE";
        return !isOnLoan && canCirculate;
      });

      const availableCopies = availableItems.length;

      // Get ALL medium types from ALL items
      const allMediumTypes = [
        ...new Set(
          items
            .map((item) => {
              const medium = this.getMediumDisplay(item, metadata);
              return medium !== "Not specified" ? medium : null;
            })
            .filter((medium) => medium)
        ),
      ];

      // Get medium types from AVAILABLE items only
      const availableMediumTypes = [
        ...new Set(
          availableItems
            .map((item) => {
              const medium = this.getMediumDisplay(item, metadata);
              return medium !== "Not specified" ? medium : null;
            })
            .filter((medium) => medium)
        ),
      ];

      // Check if any items are overbooked
      const isOverbooked = metadata.circulation?.overbooked || false;

      // Determine color based on availability
      let labelColor = "green";
      if (availableCopies === 0) {
        labelColor = "red";
      } else if (isOverbooked) {
        labelColor = "blue";
      }

      // If no copies available at all (neither physical nor electronic)
      if (availableCopies === 0 && !hasElectronicItems) {
        return (
          <div className="circulation-info-container circulation-info-fixed">
            <div className="circulation-info-with-icons">
              {allMediumTypes.length > 0 && (
                <div className="available-medium-icons">
                  {allMediumTypes.map((mediumDisplay) => {
                    const config = this.getMediumTypeConfig(mediumDisplay);
                    return (
                      <Icon
                        key={mediumDisplay}
                        name={config.icon}
                        color="red"
                        title={`${config.title} - not available`}
                        className="available-medium-icon unavailable-medium"
                      />
                    );
                  })}
                </div>
              )}

              <Label basic color="red" className="circulation-label">
                <Icon name="book" />
                <span className="circulation-text">0 copy available</span>
              </Label>
            </div>
          </div>
        );
      }

      // If we have available physical items
      if (allMediumTypes.length > 0) {
        return (
          <div className="circulation-info-container circulation-info-fixed">
            <div className="circulation-info-with-icons">
              <div className="available-medium-icons">
                {allMediumTypes.map((mediumDisplay) => {
                  const config = this.getMediumTypeConfig(mediumDisplay);
                  const isAvailable =
                    availableMediumTypes.includes(mediumDisplay);

                  return (
                    <Icon
                      key={mediumDisplay}
                      name={config.icon}
                      color={isAvailable ? config.color : "grey"}
                      title={`${config.title}${
                        isAvailable ? " - available" : " - not available"
                      }`}
                      className={`available-medium-icon ${
                        isAvailable ? "available-medium" : "unavailable-medium"
                      }`}
                    />
                  );
                })}
              </div>

              <Label basic color={labelColor} className="circulation-label">
                <Icon name="book" />
                <span className="circulation-text">
                  {isOverbooked && availableCopies > 0 ? (
                    "Add to waiting list"
                  ) : (
                    <>
                      {availableCopies}
                      {availableCopies === 1 ? " copy" : " copies"} available
                    </>
                  )}
                </span>
              </Label>
            </div>
          </div>
        );
      }

      // Fallback for physical items without medium info
      return (
        <div className="circulation-info-container circulation-info-fixed">
          <Label basic color="green" className="circulation-label">
            <Icon name="check circle" color="green" />
            <span className="circulation-text">
              {availableCopies}
              {availableCopies === 1 ? " copy" : " copies"} available
            </span>
          </Label>
        </div>
      );
    }

    // Handle ONLY electronic items (no physical items)
    if (!hasPhysicalItems && hasElectronicItems) {
      const uniqueEItemTypes = [
        ...new Set(eitems.hits.map((eitem) => eitem.eitem_type)),
      ];

      return (
        <div className="circulation-info-container circulation-info-fixed">
          <div className="circulation-info-with-icons">
            <div className="available-medium-icons">
              {uniqueEItemTypes.map((type) => {
                const config = this.getMediumTypeConfig(type);
                return (
                  <Icon
                    key={type}
                    name={config.icon}
                    color={config.color}
                    title={`${config.title} available electronically`}
                    className="available-medium-icon"
                  />
                );
              })}
            </div>

            <Label basic color="green" className="circulation-label">
              <Icon name="check circle" color="green" />
              <span className="circulation-text">Available electronically</span>
            </Label>
          </div>
        </div>
      );
    }

    // Final fallback
    return (
      <div className="circulation-info-container circulation-info-fixed">
        <Label basic color="grey" className="circulation-label">
          <Icon name="question circle" />
          <span className="circulation-text">Status unknown</span>
        </Label>
      </div>
    );
  };

  getAllLocationInfo = (metadata) => {
    const locations = [];

    // Use the live items data
    const items = metadata.items?.hits || [];

    if (!items.length) {
      return locations;
    }

    // Get location info from first 2 items for the floating labels
    for (let index = 0; index < Math.min(items.length, 2); index++) {
      const item = items[index];

      // Extract shelf and rack information
      const shelf = item.shelf?.trim() || null;
      const rack =
        item.rack?.trim() || item.internal_location?.rack?.trim() || null;

      // Only add if we have shelf or rack information
      if (shelf || rack) {
        locations.push({
          shelf: shelf,
          rack: rack,
          itemIndex: index,
          location_name:
            item.internal_location?.name ||
            item.internal_location?.location?.name ||
            "Unknown Location",
        });
      }
    }
    return locations;
  };

  // Render floating location labels with smart positioning
  renderFloatingLocationLabels = (metadata) => {
    const locations = this.getAllLocationInfo(metadata);
    if (locations.length === 0) return null;
    const { showLocationInfo } = this.state;
    const totalLocations = metadata.items?.hits?.length || 0;
    const hasMoreLocations = totalLocations > 2;

    return (
      <div
        className={`fs-floating-location-labels ${
          showLocationInfo ? "show" : ""
        }`}
      >
        {locations.map((location, index) => (
          <div key={index} className="fs-label-group">
            {location.shelf && (
              <Label color="blue" size="mini" className="fs-label-small">
                <Icon name="warehouse" size="tiny" />
                Shelf: {location.shelf}
              </Label>
            )}
            {location.rack && (
              <Label color="green" size="mini" className="fs-label-small">
                <Icon name="grid layout" size="tiny" />
                Rack: {location.rack}
              </Label>
            )}
          </div>
        ))}
        {hasMoreLocations && (
          <Label color="grey" size="mini" className="fs-label-more">
            ... see more details
          </Label>
        )}
      </div>
    );
  };

  render() {
    const { data } = this.props;
    const { metadata } = data;
    const url = FrontSiteRoutes.documentDetailsFor(metadata.pid);
    let authors = "";
    if (data.metadata.authors && Array.isArray(data.metadata.authors)) {
      authors = data.metadata.authors
        .slice(0, invenioConfig.LITERATURE.authors.maxDisplay)
        .map((elem) => elem["full_name"])
        .join("; ");
      if (data.metadata.other_authors) {
        authors = authors + " et al.";
      }
    }

    const volume = _get(metadata, "relations.multipart_monograph[0].volume");
    const multipartTitle = _get(
      metadata,
      "relations.multipart_monograph[0].record_metadata.title",
      ""
    );
    const subtitle = renderSubtitle(metadata.alternative_titles);

    return (
      <Overridable id="DocumentCard.layout" {...this.props}>
        <Card
          centered
          className="fs-book-card"
          as={Link}
          to={url}
          data-test={metadata.pid}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          <Card.Meta className="discrete">{metadata.document_type}</Card.Meta>

          <Overridable id="DocumentCard.cover" {...this.props}>
            {this.renderImage()}
          </Overridable>

          {/* Floating location labels */}
          {this.renderFloatingLocationLabels(metadata)}

          <Card.Content>
            <Card.Header>
              <LiteratureTitle title={metadata.title} />
            </Card.Header>
            <Card.Meta>
              {subtitle}
              <Truncate lines={1}>{authors}</Truncate>
              <Overridable id="DocumentCard.AfterAuthors" metadata={metadata} />
              <div>
                {metadata.publication_year}
                {metadata.edition && <> - Edition {metadata.edition}</>}
                {volume && <> - Vol. {volume}</>}
              </div>

              {/* Sections and Call Number */}
              {metadata.sections && metadata.call_number && (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {metadata.sections} | {metadata.call_number}
                </div>
              )}

              <div>
                {multipartTitle}
                {metadata.imprint?.publisher && (
                  <>
                    {" "}
                    <br /> {metadata.imprint.publisher}
                  </>
                )}
              </div>
              {/* Subjects */}
              {metadata.subjects && metadata.subjects.length > 0 && (
                <div style={{ marginTop: "5px" }}>
                  <Icon name="tag" size="small" />
                  {metadata.subjects.map((subject, index) => (
                    <span key={index}>
                      {subject.value}
                      {index < metadata.subjects.length - 1 && ", "}
                    </span>
                  ))}
                </div>
              )}
            </Card.Meta>
          </Card.Content>
          <Card.Content extra>
            <div className="card-labels-container">
              {/* Circulation info fixed at bottom */}
              {this.renderCirculationInfo(metadata)}
            </div>
            <Overridable id="DocumentCard.Extras" metadata={metadata} />
          </Card.Content>
        </Card>
      </Overridable>
    );
  }
}

DocumentCard.propTypes = {
  data: PropTypes.object.isRequired,
  volume: PropTypes.string,
};

DocumentCard.defaultProps = {
  volume: null,
};

export default Overridable.component("DocumentCard", DocumentCard);
